import React, { useCallback, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, ExternalLink } from 'lucide-react';

// Full-screen photo viewer. items: [{ src, label, caption }]
export function PhotoLightbox({ items, index, onIndex, onClose }) {
  const touchX = useRef(null);
  const count = items.length;
  const prev = useCallback(() => onIndex((index - 1 + count) % count), [index, count, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % count), [index, count, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next, onClose]);

  if (!count || index == null) return null;
  const item = items[index];

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (dx > 50) prev(); else if (dx < -50) next();
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/95 flex flex-col select-none" onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="photo-lightbox">
      <div className="flex items-center justify-between px-4 py-3 text-white/90 text-sm" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0">
          <span className="font-bold capitalize" data-testid="lightbox-label">{item.label}</span>
          {item.caption && <span className="text-white/60 ml-2 truncate">{item.caption}</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-white/60 text-xs" data-testid="lightbox-counter">{index + 1} / {count}</span>
          <a href={item.src} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20" data-testid="lightbox-open-original"><ExternalLink className="w-3.5 h-3.5" /> Open original</a>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md bg-white/10 hover:bg-white/20" aria-label="Close" data-testid="lightbox-close"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="flex-1 relative flex items-center justify-center px-14 pb-4 min-h-0">
        {count > 1 && (
          <button type="button" onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/25 text-white" aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft className="w-7 h-7" /></button>
        )}
        <img key={item.src} src={item.src} alt={item.label} onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full object-contain rounded-md shadow-2xl animate-in fade-in zoom-in-95 duration-200" data-testid="lightbox-image" />
        {count > 1 && (
          <button type="button" onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/25 text-white" aria-label="Next" data-testid="lightbox-next"><ChevronRight className="w-7 h-7" /></button>
        )}
      </div>
      {count > 1 && (
        <div className="flex justify-center gap-2 pb-4" onClick={(e) => e.stopPropagation()}>
          {items.map((it, i) => (
            <button key={it.src} type="button" onClick={() => onIndex(i)} className={`rounded overflow-hidden border-2 transition-opacity ${i === index ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`} data-testid={`lightbox-thumb-${i}`}>
              <img src={it.src} alt={it.label} className="w-14 h-10 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
